import { FC, useState } from "react";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  Box,
  IconButton,
  Typography,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";

import { Colors } from "../../../configs";
import { pxToRem } from "../../../util";
import { CustomTextBox, CustomButton } from "../../../components/atoms";
import { CustomSelectBox } from "../../../components/atoms/custom-select-box/custom-select-box";
import CustomRadioButton from "../../../components/atoms/radio-button/custom-radio-button";
import { NewCustomModalDrawer } from "../../../components/atoms/custom-drawer/new-custom-modal-drawer";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { updateBillDiscountAction } from "../../../redux/actions/cartAction";
import { DiscountMode } from "../../../typings";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";

interface IDiscountDrawerBill {
  id: number;
  subtotal: number;
}

interface IDiscountDrawerProps {
  isOpen: boolean;
  toggleDrawer: () => void;
  containerRef?: HTMLElement | null;
  bills: IDiscountDrawerBill[];
}

const NONE_DISCOUNT_ID = "none";

// Same percentage/fixed math useCart.ts's calculateDiscountAmount uses for
// the check-in Discount modal, applied against a single bill's subtotal
// instead of a live cart session.
const calculateDiscountAmount = (
  subtotal: number,
  mode: DiscountMode,
  discountAmount: number
) => {
  if (subtotal <= 0) return 0;
  if (mode === DiscountMode.PERCENTAGE) {
    return Number(((subtotal * discountAmount) / 100).toFixed(2));
  }
  return Math.min(discountAmount, subtotal);
};

const DiscountDrawer: FC<IDiscountDrawerProps> = ({
  isOpen,
  toggleDrawer,
  containerRef,
  bills,
}) => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  const { getDiscountByStoreResponse } = useAppSelector(
    (state) => state.discount
  );

  const [discountType, setDiscountType] = useState<"defined" | "custom">(
    "defined"
  );
  const [definedDiscountId, setDefinedDiscountId] =
    useState<string>(NONE_DISCOUNT_ID);
  const [customDiscountType, setCustomDiscountType] = useState<
    "percentage" | "fixed" | "free"
  >("percentage");
  const [customAmount, setCustomAmount] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  const definedDiscounts =
    getDiscountByStoreResponse?.body.filter((d) => d.enabled) ?? [];
  const selectedDefinedDiscount = definedDiscounts.find(
    (d) => d.id === definedDiscountId
  );

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDiscountType(event.target.value as "defined" | "custom");
  };

  const handleDefinedDiscountChange = (e: SelectChangeEvent<unknown>) => {
    setDefinedDiscountId(e.target.value as string);
  };

  const handleCustomDiscountTypeChange = (e: SelectChangeEvent<unknown>) => {
    setCustomDiscountType(e.target.value as "percentage" | "fixed" | "free");
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomAmount(e.target.value);
  };

  const canSubmit =
    bills.length > 0 &&
    !submitting &&
    (discountType === "defined"
      ? !!selectedDefinedDiscount
      : customDiscountType === "free" || Number(customAmount) > 0);

  const handleAddDiscount = async () => {
    if (!canSubmit) return;

    let mode: DiscountMode;
    let amount: number;
    let discountId: number | null;

    if (discountType === "defined" && selectedDefinedDiscount) {
      mode = selectedDefinedDiscount.discountMode;
      amount = selectedDefinedDiscount.discountAmount;
      discountId = Number(selectedDefinedDiscount.id);
    } else {
      mode =
        customDiscountType === "fixed"
          ? DiscountMode.FIXED
          : DiscountMode.PERCENTAGE;
      amount = customDiscountType === "free" ? 100 : Number(customAmount);
      discountId = null;
    }

    setSubmitting(true);
    try {
      await Promise.all(
        bills.map((bill) =>
          dispatch(
            updateBillDiscountAction({
              billId: bill.id,
              discountAmount: calculateDiscountAmount(
                bill.subtotal,
                mode,
                amount
              ),
              discountId,
            })
          ).unwrap()
        )
      );
      snackbar.showSnackbar("Discount applied", "success");
      setDefinedDiscountId(NONE_DISCOUNT_ID);
      setCustomAmount("");
      toggleDrawer();
    } catch (err: any) {
      snackbar.showSnackbar(
        typeof err === "string" ? err : "Failed to apply discount",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const LABEL_COL = pxToRem(140);
  const INPUT_COL = pxToRem(270);

  return (
    <NewCustomModalDrawer
      isOpen={isOpen}
      toggleDrawer={toggleDrawer}
      containerRef={containerRef}
      width={pxToRem(530)}
      maxWidth="90vw"
      sx={{
        background: Colors.WHITE,
        minHeight: "100%",
        width: pxToRem(530),
        display: "flex",
        flexDirection: "column",
        padding: pxToRem(20),
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: pxToRem(20),
          marginTop: pxToRem(20),
          marginLeft: pxToRem(20),
          marginRight: pxToRem(10),
        }}
      >
        <Typography
          sx={{
            fontSize: "var(--fs-lg)",
            fontWeight: 600,
            color: Colors.TEXT_GRAY_DARK,
          }}
        >
          Discounts
        </Typography>
        <IconButton
          onClick={toggleDrawer}
          sx={{ color: Colors.TEXT_GRAY_DARK }}
        >
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          marginLeft: pxToRem(25),
          marginBottom: pxToRem(20),
        }}
      >
        <CustomRadioButton
          radioItems={[
            { value: "defined", label: "Defined Discount" },
            { value: "custom", label: "Custom Discount" },
          ]}
          orientation="row"
          justify="space-between"
          defaultValue={discountType}
          onChange={handleTypeChange}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: pxToRem(20),
          marginLeft: pxToRem(25),
          marginRight: pxToRem(25),
        }}
      >
        {discountType === "defined" ? (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `${LABEL_COL} ${INPUT_COL}`,
                alignItems: "center",
                gap: pxToRem(20),
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "var(--fs-base)",
                  color: Colors.TEXT_GRAY_DARK,
                  textAlign: "left",
                }}
              >
                Discount
              </Typography>
              <CustomSelectBox
                value={definedDiscountId}
                onChange={handleDefinedDiscountChange}
                sx={{
                  width: "100%",
                  "& .MuiSelect-select": {
                    color: Colors.TEXT_GRAY_DARK,
                    fontSize: "var(--fs-base)",
                  },
                }}
              >
                <MenuItem value={NONE_DISCOUNT_ID}>None</MenuItem>
                {definedDiscounts.map((discount) => (
                  <MenuItem key={discount.id} value={discount.id}>
                    {discount.discountName}
                  </MenuItem>
                ))}
              </CustomSelectBox>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `${LABEL_COL} ${INPUT_COL}`,
                alignItems: "flex-start",
                gap: pxToRem(20),
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "var(--fs-base)",
                  color: Colors.TEXT_GRAY_DARK,
                  textAlign: "left",
                  mt: pxToRem(5),
                }}
              >
                Description
              </Typography>
              <Typography
                sx={{
                  fontSize: "var(--fs-base)",
                  color: Colors.TEXT_GRAY_DARK,
                  opacity: 0.6,
                }}
              >
                {selectedDefinedDiscount?.description ?? "—"}
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `${LABEL_COL} ${INPUT_COL}`,
                alignItems: "center",
                gap: pxToRem(20),
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "var(--fs-base)",
                  color: Colors.TEXT_GRAY_DARK,
                  textAlign: "left",
                }}
              >
                Discount Type
              </Typography>
              <CustomSelectBox
                value={customDiscountType}
                onChange={handleCustomDiscountTypeChange}
                sx={{
                  width: "100%",
                  "& .MuiSelect-select": {
                    color: Colors.TEXT_GRAY_DARK,
                    fontSize: "var(--fs-base)",
                  },
                }}
              >
                <MenuItem value="percentage">Percentage</MenuItem>
                <MenuItem value="fixed">Fixed cash reduction</MenuItem>
                <MenuItem value="free">Free Service</MenuItem>
              </CustomSelectBox>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `${LABEL_COL} ${INPUT_COL}`,
                alignItems: "center",
                gap: pxToRem(20),
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "var(--fs-base)",
                  color: Colors.TEXT_GRAY_DARK,
                  textAlign: "left",
                }}
              >
                Amount
              </Typography>
              <CustomTextBox
                type="number"
                placeholder="0"
                disabled={customDiscountType === "free"}
                styles={{
                  width: "100%",
                  fontSize: "var(--fs-base)",
                  color: Colors.TEXT_GRAY_DARK,
                }}
                value={customDiscountType === "free" ? "100" : customAmount}
                onChange={handleAmountChange}
                InputProps={{
                  endAdornment: (
                    <Typography
                      sx={{
                        color: Colors.TEXT_GRAY_DARK,
                        fontSize: "var(--fs-base)",
                        mr: pxToRem(1),
                      }}
                    >
                      {customDiscountType === "fixed" ? "$" : "%"}
                    </Typography>
                  ),
                }}
              />
            </Box>
          </>
        )}
      </Box>

      <CustomButton
        onClick={handleAddDiscount}
        disabled={!canSubmit}
        sx={{
          alignSelf: "center",
          background: Colors.PRIMARY,
          color: Colors.WHITE,
          borderRadius: pxToRem(10),
          width: pxToRem(424),
          height: pxToRem(60),
          fontSize: "var(--fs-base)",
          marginBottom: pxToRem(15),
          marginTop: pxToRem(30),
        }}
      >
        {submitting ? "Applying..." : "Add Discount"}
      </CustomButton>
    </NewCustomModalDrawer>
  );
};

export default DiscountDrawer;
